import React from 'react'
import styled from 'styled-components'
import { useHandleCopyValue } from '@sketch/modules-common'
import {
  AttributeList,
  FullCopyAttribute,
  CollapsibleInspectorSection,
  AttributeLabel,
  AttributeValue,
} from '../../components'
import { roundWithLocale } from '@sketch/utils'
import copy from './copy'
import { PRLayerSizingBehaviourEnum } from '@sketch-hq/sketch-web-renderer'
import { MeasureSizingBehaviour } from '../MeasureSizingBehaviour'
import { BoxModel } from '../../../BoxModel'
import {
  ElementType,
  SketchFrameElement,
  SketchLayerElement,
  SketchSectionElement,
} from '../../../../../../inspector'
import { ReactComponent as RectanglesIcon } from '@sketch/icons/rectangles-2-17'

const PositionContainer = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 16px;
`

const MinMaxContainer = styled.div`
  columns: 2;

  > div:nth-child(2) {
    ${AttributeLabel} {
      width: auto;
      margin-right: 8px;
    }

    ${AttributeValue} {
      width: 100%;
    }
  }
`

const PositionLabel = styled.span`
  color: ${({ theme }) => theme.colors.foreground.secondary.D};
  margin-left: 4px; /* space between value and X/Y label */
`

interface PositionAndSizeProps {
  rotate?: number
  x: number
  y: number
  width: number
  widthBehaviour?: PRLayerSizingBehaviourEnum
  height: number
  heightBehaviour?: PRLayerSizingBehaviourEnum
  element?: SketchLayerElement | SketchFrameElement | SketchSectionElement
  // When a layer forms part of a stack, the user can define a maximum and minimum
  // width/height for the layer.
  minWidth?: number
  maxWidth?: number
  minHeight?: number
  maxHeight?: number
  // When a layer has relative sizing for a specific axis the user is able
  // to express a layer width/heigh as a percetage.
  percentWidth?: number
  percentHeight?: number
}

export const PositionAndSize = ({
  rotate = 0,
  x,
  y,
  width,
  minWidth,
  maxWidth,
  height,
  minHeight,
  maxHeight,
  percentWidth,
  percentHeight,
  widthBehaviour,
  heightBehaviour,
  element,
}: PositionAndSizeProps) => {
  const roundedX = roundWithLocale(x, 2)
  const roundedY = roundWithLocale(y, 2)
  // The raw rotate value in the pr file works counter-clockwise, but users think
  // in clockwise rotation, so flip the sign
  const reversedRotate = rotate !== 0 ? -rotate : rotate
  const roundedRotate = `${roundWithLocale(reversedRotate, 2)}°`
  const roundedWidth = roundWithLocale(width, 2)
  const roundedHeight = roundWithLocale(height, 2)

  const roundedMinWidth = minWidth ? roundWithLocale(minWidth, 2) : null
  const roundedMaxWidth = maxWidth ? roundWithLocale(maxWidth, 2) : null
  const roundedMinHeight = minHeight ? roundWithLocale(minHeight, 2) : null
  const roundedMaxHeight = maxHeight ? roundWithLocale(maxHeight, 2) : null

  const roundedPercentWidth = percentWidth
    ? roundWithLocale(percentWidth * 100, 0)
    : null

  const roundedPercentHeight = percentHeight
    ? roundWithLocale(percentHeight * 100, 0)
    : null

  const sectionCopyValue = copy({
    rotate: roundedRotate,
    x: roundedX,
    y: roundedY,
    width: roundedPercentWidth ? `${roundedPercentWidth}%` : roundedWidth,
    height: roundedPercentHeight ? `${roundedPercentHeight}%` : roundedHeight,
  })

  const { handleCopyValue, copyTooltipText } =
    useHandleCopyValue(sectionCopyValue)

  return (
    <CollapsibleInspectorSection
      identifier="position-and-size"
      title="Position and size"
      actions={[
        {
          icon: <RectanglesIcon width={17} height={17} />,
          tooltipText: copyTooltipText,
          onClick: handleCopyValue,
        },
      ]}
      data-testid="inspector-sidebar-position"
    >
      <AttributeList>
        <FullCopyAttribute
          label="Position"
          copyValue={`x:${roundedX}, y:${roundedY}`}
          value={
            <PositionContainer>
              <span>
                {roundedX}
                <PositionLabel>X</PositionLabel>
              </span>
              <span>
                {roundedY}
                <PositionLabel>Y</PositionLabel>
              </span>
            </PositionContainer>
          }
        />
        <FullCopyAttribute label="Rotate" value={roundedRotate} />
        <FullCopyAttribute
          label="Width"
          copyValue={
            roundedPercentWidth ? `${roundedPercentWidth}%` : roundedWidth
          }
          value={
            <>
              {roundedPercentWidth ? `${roundedPercentWidth}%` : roundedWidth}{' '}
              <MeasureSizingBehaviour behaviour={widthBehaviour} />
            </>
          }
        />

        {(roundedMinWidth || roundedMaxWidth) && (
          <MinMaxContainer>
            {roundedMinWidth && (
              <FullCopyAttribute label="Minimum" value={roundedMinWidth} />
            )}
            {roundedMaxWidth && (
              <FullCopyAttribute label="Maximum" value={roundedMaxWidth} />
            )}
          </MinMaxContainer>
        )}

        <FullCopyAttribute
          label="Height"
          copyValue={
            roundedPercentHeight ? `${roundedPercentHeight}%` : roundedHeight
          }
          value={
            <>
              {roundedPercentHeight
                ? `${roundedPercentHeight}%`
                : roundedHeight}{' '}
              <MeasureSizingBehaviour behaviour={heightBehaviour} rotate />
            </>
          }
        />

        {(roundedMinHeight || roundedMaxHeight) && (
          <MinMaxContainer>
            {roundedMinHeight && (
              <FullCopyAttribute label="Minimum" value={roundedMinHeight} />
            )}
            {roundedMaxHeight && (
              <FullCopyAttribute label="Maximum" value={roundedMaxHeight} />
            )}
          </MinMaxContainer>
        )}
      </AttributeList>

      {element && element.type !== ElementType.Group && (
        <BoxModel element={element} />
      )}
    </CollapsibleInspectorSection>
  )
}
